import type { JSX } from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  addToast,
  Button,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useCreateTeam } from '~/hooks/useTeams';
import { useTenant } from '~/hooks/useTenant';
import { generateSlug } from '~/utils/slug';

import { Input, Textarea } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal } from '~/components/Modal';

/**
 * Zod schema for team creation form.
 * Matches backend constraints.
 */
const createTeamSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, 'Team name must be at least 3 characters')
    .max(255, 'Team name must be less than 255 characters'),
  slug: z
    .string()
    .trim()
    .min(3, 'Slug must be at least 3 characters')
    .max(100, 'Slug must be less than 100 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional()
    .or(z.literal('')),
});

type CreateTeamFormData = z.infer<typeof createTeamSchema>;

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateTeamModal({ isOpen, onClose }: CreateTeamModalProps): JSX.Element {
  const { currentOrg } = useTenant();
  const createTeam = useCreateTeam();

  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<CreateTeamFormData>({
    resolver: zodResolver(createTeamSchema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      slug: '',
      description: '',
    },
  });

  // Extract ref/onBlur from register (onChange is filtered by HeroUI, so we use onValueChange)
  const { onBlur: nameOnBlur, ref: nameRef } = register('name');
  const { onBlur: slugOnBlur, ref: slugRef } = register('slug');

  const name = watch('name');
  const slug = watch('slug');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = (data: CreateTeamFormData): void => {
    if (!currentOrg) return;

    createTeam.mutate(
      {
        orgId: currentOrg.id,
        data: {
          name: data.name,
          slug: data.slug,
          description: data.description || undefined,
        },
      },
      {
        onSuccess: () => {
          addToast({
            description: `Team "${data.name}" created successfully`,
            color: 'success',
          });
          onClose();
        },
        onError: (error: Error) => {
          // Handle 409 Conflict (slug already exists)
          if (error.message.includes('already exists') || error.message.includes('409')) {
            setError('slug', {
              type: 'manual',
              message: 'This slug is already taken. Try a different one.',
            });
          } else {
            addToast({
              description: error.message || 'Failed to create team',
              color: 'danger',
            });
          }
        },
      },
    );
  };

  const handleClose = (): void => {
    if (!createTeam.isPending) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleClose}
      maxWidth="lg"
      preventClosing={createTeam.isPending}
    >
      <ModalContent>
        <form noValidate onSubmit={e => void handleSubmit(onSubmit)(e)}>
          <ModalHeader>Create Team</ModalHeader>
          <Divider />
          <ModalBody>
            <Input
              id="team-name"
              name="name"
              ref={nameRef}
              labelPlacement="outside-top"
              label="Team Name"
              placeholder="Engineering"
              description="Display name for your team (3-255 characters)"
              isRequired
              isInvalid={!!errors.name}
              errorMessage={errors.name?.message}
              value={name}
              onValueChange={value => {
                void setValue('name', value, { shouldDirty: true });
              }}
              onBlur={e => {
                void nameOnBlur(e);
                // Auto-generate slug on blur if name valid and slug empty
                const trimmedName = name?.trim() || '';
                if (trimmedName.length >= 3 && !slug) {
                  setValue('slug', generateSlug(trimmedName), { shouldValidate: true });
                }
              }}
            />

            <Input
              id="team-slug"
              name="slug"
              ref={slugRef}
              labelPlacement="outside-top"
              label="Team Slug"
              placeholder="engineering"
              description="URL-friendly identifier (lowercase, numbers, hyphens only)"
              isRequired
              isInvalid={!!errors.slug}
              errorMessage={errors.slug?.message}
              value={slug}
              onValueChange={value => {
                void setValue('slug', value, { shouldDirty: true });
              }}
              onBlur={e => {
                void slugOnBlur(e);
              }}
            />

            <Textarea
              id="team-description"
              label="Description"
              placeholder="Describe your team (optional)"
              description="Brief description of the team's purpose"
              isInvalid={!!errors.description}
              errorMessage={errors.description?.message}
              {...register('description')}
            />
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Button variant="light" onPress={handleClose} isDisabled={createTeam.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              isDisabled={createTeam.isPending || !isDirty}
              data-testid="create-team-submit"
            >
              {createTeam.isPending ? <LoadingSpinner /> : 'Create Team'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
